import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import LiveEditing from './Components/LiveEditing';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import FavoriteProductsIcon from './Components/FavoriteProductsIcon';
import FavoriteIcon from './Components/FavoriteIcon';
import SubNavigation from './Components/SubNavigation';
import MainImageContent from "./Components/MainImageContent.js";
import MultiCarousel from "./Components/MultiCarousel.js";
import FavoriteProducts from "./Components/FavoriteProducts.js";
import Product from './Components/ProductPage/Product';
import Squeezebox from './Components/Squeezebox';
import StringBuilder from './Components/StringBuilder.js';
import ConversionTracking from './Components/ConversionTracking.js';
import BrandInfo from './Components/ProductPage/BrandInfo.js';
import ToggleVAT from './Components/ToggleVAT.js';
import FooterVATToggle from './Components/FooterVATToggle.js';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('liveEditing')) {
        renderReact(
            <Provider store={store}>
                <LiveEditing />
            </Provider>,
            document.getElementById('liveEditing')
        );
    }
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('navbar')) {
      const { searchType, favoritePageUrl, hideSearch, showpricewithvat } =
      document.getElementById("navbar").dataset;
        renderReact(
            <Provider store={store}>
                <Navigation searchType={searchType}
          favoritePageUrl={favoritePageUrl} hideSearch={hideSearch === "True"} showPriceWithVAT={showpricewithvat === "True"} />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Components/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Components/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Components/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(
            ({ lightboxImages }) => {
                store.injectReducer('lightboxImages', lightboxImages);
                const rootElement = document.getElementById('lightBoxImages');
                const images = Array.from(
                    rootElement.querySelectorAll('template')
                ).map((img) => ({
                    html: img.innerHTML,
                    src: img.dataset.src,
                }));
                renderReact(
                    <Provider store={store}>
                        <LightboxImages images={images} />
                    </Provider>,
                    document.getElementById('lightBoxImages')
                );
            }
        );
    }

    if (document.querySelectorAll(".slider").length > 0) {
      const Slider = DynamicComponent({
        loader: () => import("./Components/Slider"),
      });
      Array.from(document.querySelectorAll(".slider")).forEach(
        (slider, index) => {
          const values = [...slider.querySelectorAll(".slider__block")].map(
            (block) => {
              return {
                htmlImage: block.dataset.htmlImage,
                url: block.dataset.url,
                text: block.dataset.text,
                pretitle: block.dataset.pretitle,
                withOverlay: block.dataset.withOverlay.toLowerCase() === "true",
                lightText: block.dataset.lightText.toLowerCase() === "true",
                textcolor: block.dataset.textcolor,
              };
            }
          );
          if (values.length > 0) {
            ReactDOM.render(<Slider values={values} />, slider);
          }
        }
      );
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach(
            (button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    href,
                    cssClass,
                    label,
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <BuyButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                href,
                                cssClass,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () => import('./Components/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }

    var headerFavorite = document.getElementById("favoriteIcon");
    if (headerFavorite) {
      const { favoritePageUrl } = headerFavorite.dataset;
      ReactDOM.render(
        <Provider store={store}>
          <FavoriteProductsIcon {...{ favoritePageUrl }} />
        </Provider>,
        document.getElementById("favoriteIcon")
      );
    }
  
    var conversionTrackingElement = document.getElementById(
      "conversionTracking"
    );
    if (conversionTrackingElement) {
      const data = conversionTrackingElement.dataset;
  
      ReactDOM.render(
        <Provider store={store}>
          <ConversionTracking order={data.order} />
        </Provider>,
        conversionTrackingElement
      );
    }

    if (document.querySelectorAll(".favorite-icon").length > 0) {
        Array.from(document.querySelectorAll(".favorite-icon")).forEach(
          (favoriteicon) => {            
            const { articleNumber } = favoriteicon.dataset;
            ReactDOM.render(
              <Provider store={store}>
                <FavoriteIcon articleNumber={articleNumber} />
              </Provider>,
              favoriteicon
            );
          }
        );
      }

      if (document.getElementById("subNavigation")) {
        const SubNavigation = DynamicComponent({
          loader: () => import("./Components/SubNavigation"),
        });
        const links = document.getElementById("subNavigation").dataset.links;
        ReactDOM.render(
          <Provider store={store}>
            <SubNavigation links={links} />
          </Provider>,
          document.getElementById("subNavigation")
        );
      }

      if (document.getElementById("mainImageContent")) {
        const data = document.getElementById("mainImageContent").dataset;
    
        ReactDOM.render(
          <Provider store={store}>
            <MainImageContent currentPageDescriptionEditor={JSON.parse(data.descriptioneditor)} currentPageDescription={data.description}
            textColor={data.textcolor}/>
          </Provider>,
          document.getElementById("mainImageContent")
        );
      }

      if (document.querySelectorAll(".multi-carousel").length > 0) {
        Array.from(document.querySelectorAll(".multi-carousel")).forEach(
          (block) => {            
            const model =
              block.dataset && block.dataset.items
                ? JSON.parse(block.dataset.items)
                : null;
            const { itemType } = block.dataset;
            ReactDOM.render(
              <Provider store={store}>
                <MultiCarousel items={model} itemType={itemType} />
              </Provider>,
              block
            );
          }
        );
      }

      if (document.querySelectorAll(".favorite-products-react").length > 0) {
        Array.from(document.querySelectorAll(".favorite-products-react")).forEach(
          (favoriteProducts) => {            
    
            ReactDOM.render(
              <Provider store={store}>
                <FavoriteProducts />
              </Provider>,
              favoriteProducts
            );
          }
        );
      }

      if (document.getElementById("product")) {
        const data = document.getElementById("product").dataset;
    
        ReactDOM.render(
          <Provider store={store}>
            <Product product={data.product} isMobileDevice={data.mobile}/>
          </Provider>,
          document.getElementById("product")
        );
      }

      if (document.querySelectorAll(".squeezeboxContainer").length > 0) {
        Array.from(document.querySelectorAll(".squeezeboxContainer")).forEach(
          (squeezebox) => {
            Array.from(squeezebox.querySelectorAll(".squeezebox-item")).forEach(
              (row, index) => {
                const { title, isOpen } = row.dataset;
                const html = row.innerHTML;
                ReactDOM.render(<Squeezebox {...{ title, html, isOpen }} />, row);
              }
            );
          }
        );
      }

      if (document.querySelectorAll(".testimonials").length > 0) {
        const TestimonialSlider = DynamicComponent({
          loader: () => import("./Components/TestimonialSlider"),
        });
        Array.from(document.querySelectorAll(".testimonials")).forEach(
          (testimonial) => {
            const items = Array.from(
              testimonial.querySelectorAll(".testimonial-slider")
            ).map((elem) => {
              return JSON.parse(elem.dataset.items);
            });
            if (items.length > 0) {
              ReactDOM.render(<TestimonialSlider items={items} />, testimonial);
            }
          }
        );
      }

      if (document.getElementById("string-builder")) {
        const data = document.getElementById("string-builder").dataset;
    
        if (data.guid !== undefined) {
          ReactDOM.render(
            <Provider store={store}>
              <StringBuilder
                guid={data.guid}
                collapsible={data.collapsible}
                stringImageHtml={data.StringImageHtml}
                textColor={data.textColor}
                countryCode={data.countryCode}
              />
            </Provider>,
            document.getElementById("string-builder")
          );
        }        
      }


      if (document.getElementById("product-brand-info")) {
        const data = document.getElementById("product-brand-info").dataset;
        
        if (data.brandurl) {
          ReactDOM.render(
            <Provider store={store}>
              <BrandInfo
                brandinfo={
                  {
                    textColor: data.textcolor, 
                    backgroundColor: data.background,
                    brandImageUrl: data.brandimageurl,
                    pageTitle: data.pagetitle,
                    title: data.title,
                    text:  JSON.parse(data.text),
                    logoUrl: data.logo,
                    brandUrl: data.brandUrl,
                  }}
              />
            </Provider>,
            document.getElementById("product-brand-info")
          );  
        }                    
      }

      if (document.getElementById("priceVatToggle")) {            
        ReactDOM.render(
          <Provider store={store}>
            <ToggleVAT />
          </Provider>,
          document.getElementById("priceVatToggle")
        );
      }

      if (document.getElementById("footerVatToggle")) {            
        ReactDOM.render(
          <Provider store={store}>
            <FooterVATToggle />
          </Provider>,
          document.getElementById("footerVatToggle")
        );
      }
};


bootstrapComponents();
